import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HeatEmissionRaw } from '@/api/EmissionReportModel'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DataBlock,
  NumericInputGroup
} from '@/common'
import Big from 'big.js'
import { UnitLabel } from '@/shared/config/config'
import { useUpdateMethodEmissionReport } from '@/hooks/api/emissionReports'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { z } from 'zod'

const heatEmissionStateSchema = z.object({
  heatImportedTj: z.string().min(1),
  heatImportedEmissionFactor: z.string().min(1),
  heatExportedTj: z.string().min(1),
  heatExportedEmissionFactor: z.string().min(1),
  heatImportedEmission: z.number(),
  heatExportedEmission: z.number()
})

export default function HeatEmissionsReportingGeneric({
  close,
  reportId,
  initialValues,
  processId
}: {
  close: () => void
  reportId: string
  initialValues: HeatEmissionRaw | null
  processId: string
}) {
  const { t } = useTranslation()
  const { checkPermission } = usePermissions()
  const canEditEmissionReport = checkPermission('emissionReport', 'update')
  const { mutateAsync: updateMethodEmissionReport, isPending } = useUpdateMethodEmissionReport(
    reportId,
    processId
  )

  const [values, setValues] = useState<HeatEmissionRaw>(
    initialValues || {
      heatImportedTj: null,
      heatImportedEmissionFactor: null,
      heatImportedEmission: null,
      heatExportedTj: null,
      heatExportedEmissionFactor: null,
      heatExportedEmission: null
    }
  )

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target
    setValues({ ...values, [name]: value })
  }

  const heatImportedEmission = useMemo(() => {
    if (!values.heatImportedTj || !values.heatImportedEmissionFactor) {
      return '0'
    }
    return Big(values.heatImportedTj).times(values.heatImportedEmissionFactor).toString()
  }, [values.heatImportedTj, values.heatImportedEmissionFactor])

  const heatExportedEmission = useMemo(() => {
    if (!values.heatExportedTj || !values.heatExportedEmissionFactor) {
      return '0'
    }
    return Big(values.heatExportedTj).times(values.heatExportedEmissionFactor).toString()
  }, [values.heatExportedTj, values.heatExportedEmissionFactor])

  const payload = useMemo(() => {
    return {
      heatImportedTj: values.heatImportedTj,
      heatImportedEmissionFactor: values.heatImportedEmissionFactor,
      heatImportedEmission: parseFloat(heatImportedEmission),
      heatExportedTj: values.heatExportedTj,
      heatExportedEmissionFactor: values.heatExportedEmissionFactor,
      heatExportedEmission: parseFloat(heatExportedEmission)
    }
  }, [values, heatImportedEmission, heatExportedEmission])

  const valuesValidation = useMemo(() => {
    const validatonResult = heatEmissionStateSchema.safeParse(payload)
    return validatonResult.success
  }, [payload])

  const handleSave = useCallback(async () => {
    if (!valuesValidation) {
      return
    }
    await updateMethodEmissionReport({
      heatEmissionRaw: payload
    })

    close()
  }, [payload, valuesValidation, updateMethodEmissionReport, close])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle title={t('report:heat.generic.import.title')} />
          </CardHeader>
          <CardBody>
            <div className="space-y-2">
              <NumericInputGroup
                name="heatImportedTj"
                required
                label={t('report:heat.generic.import.fields.heatImportedTj.label')}
                value={values.heatImportedTj || ''}
                onChange={handleChange}
                suffix={UnitLabel.TJ}
                numericFormat={{
                  allowNegative: false,
                  decimalScale: 3
                }}
              />
              <NumericInputGroup
                name="heatImportedEmissionFactor"
                required
                label={t('report:heat.generic.import.fields.heatImportedEmissionFactor.label')}
                value={values.heatImportedEmissionFactor || ''}
                onChange={handleChange}
                suffix={UnitLabel.tCO2PerTJ}
                numericFormat={{
                  allowNegative: false,
                  decimalScale: 3
                }}
              />
              <DataBlock
                label={t('report:heat.generic.import.fields.heatImportedEmission.label')}
                data={`${heatImportedEmission} ${UnitLabel.tCO2e}`}
              />
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle title={t('report:heat.generic.export.title')} />
          </CardHeader>
          <CardBody>
            <div className="space-y-2">
              <NumericInputGroup
                name="heatExportedTj"
                required
                label={t('report:heat.generic.export.fields.heatExportedTj.label')}
                value={values.heatExportedTj || ''}
                onChange={handleChange}
                suffix={UnitLabel.TJ}
                numericFormat={{
                  allowNegative: false,
                  decimalScale: 3
                }}
              />
              <NumericInputGroup
                name="heatExportedEmissionFactor"
                required
                label={t('report:heat.generic.export.fields.heatExportedEmissionFactor.label')}
                value={values.heatExportedEmissionFactor || ''}
                onChange={handleChange}
                suffix={UnitLabel.tCO2PerTJ}
                numericFormat={{
                  allowNegative: false,
                  decimalScale: 3
                }}
              />
              <DataBlock
                label={t('report:heat.generic.export.fields.heatExportedEmission.label')}
                data={`${heatExportedEmission} ${UnitLabel.tCO2e}`}
              />
            </div>
          </CardBody>
        </Card>
      </div>
      <div className="gap-2 flex justify-end">
        <Button className="min-w-24" variant="neutral" onClick={close}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          className="min-w-24"
          variant="default"
          onClick={handleSave}
          disabled={isPending || !valuesValidation || !canEditEmissionReport}
        >
          {t('common:actions.save')}
        </Button>
      </div>
    </div>
  )
}
